/**
 * Small "Top countries" horizontal bar list shown under each Editor Gross
 * Statistics card: bars = total time in the editor per country (date range +
 * country filter), tooltip = time, opens, saves, publishes.
 */

import type { EditorCountryRow } from '@/utils/analyticsCountryFilter';

function fmtTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h.toLocaleString()}h ${m}m` : `${m}m`;
}

export function EditorTopCountries({ rows, color, isDark, unavailable, hasSaves }: {
  rows: EditorCountryRow[] | undefined;
  /** bar colour (matches the editor card accent) */
  color: string;
  isDark: boolean;
  /** shown instead of bars when the country payload is missing / rebuilding */
  unavailable?: string | null;
  /** the editor has level saves/publishes (3D, 2D, Track, Coop) */
  hasSaves: boolean;
}) {
  const muted = isDark ? 'text-gray-500' : 'text-gray-400';
  const max = Math.max(1, ...(rows || []).map(r => r.seconds));
  return (
    <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700/50' : 'border-gray-200'}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Top countries · time</div>
      {unavailable ? (
        <div className={`text-[10px] ${muted}`}>{unavailable}</div>
      ) : !rows?.length ? (
        <div className={`text-[10px] ${muted}`}>No data in this range</div>
      ) : (
        <div className="space-y-1">
          {rows.map(r => {
            const tip = `${r.name}\nTime: ${fmtTime(r.seconds)}\nOpened: ${r.opens.toLocaleString()}` +
              (hasSaves ? `\nSaves: ${r.saves.toLocaleString()}\nPublishes: ${r.publishes.toLocaleString()}` : '');
            return (
              <div key={r.code} title={tip} className="cursor-default">
                <div className="flex items-center justify-between gap-1 text-[10px] leading-tight">
                  <span className={`truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{r.name}</span>
                  <span className={`shrink-0 ${muted}`}>{fmtTime(r.seconds)}</span>
                </div>
                <div className={`h-1.5 rounded-full ${isDark ? 'bg-gray-700/60' : 'bg-gray-200'}`}>
                  <div className="h-1.5 rounded-full" style={{ width: `${Math.max(3, (r.seconds / max) * 100)}%`, backgroundColor: color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
